import React from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';

import { useScreenDispatch, useScreenState } from '@gogame/screens';

import { ApplicationFormEdit } from 'scenes/Applications/components/ApplicationForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_INACTIVITY_PERIOD,
  COLUMN_IMPRESSION_ATTRIBUTION,
  COLUMN_CLICK_ATTRIBUTION,
  COLUMN_DAYS_BETWEEN_REMARKETING,
  COLUMN_TRACK_RE,
} from 'scenes/Applications/constants';
import { useLoadApplications } from 'scenes/Applications/data';

import ApplicationsDataTableToolbar from './ApplicationsDataTableToolbar';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/Applications/screenReducer';
import { getColor } from 'scenes/Applications/components/Status';
import ApplicationsActions from 'scenes/Applications/components/Actions';
import TrackRe from 'scenes/Applications/components/TrackRe';

const ApplicationsDataTable = () => {
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const [childComponent, openChildComponent] = useChildComponent();

  const { data, isLoading, isRefetching } = useLoadApplications({
    filters: screenState.filters,
    tableSettings: screenState.tableSettings,
    searchQuery: screenState.searchQuery,
  });

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(ApplicationFormEdit, {
      id,
    });
  };
  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<ApplicationsDataTableToolbar />}
        storageKey={'applications'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={75}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'text'}
          headerName={'Name'}
          width={240}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          type={'status'}
          headerName={'Status'}
          width={84}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_INACTIVITY_PERIOD}
          type={'text'}
          headerName={'Inactivity Period'}
          flex={1}
          getValue={(v = 0) => `${v} days`}
        />
        <DataGrid.Column
          field={COLUMN_IMPRESSION_ATTRIBUTION}
          type={'text'}
          headerName={'Impression Attribution'}
          flex={1}
          getValue={(v = 0) => `${v} days`}
        />
        <DataGrid.Column
          field={COLUMN_CLICK_ATTRIBUTION}
          type={'text'}
          headerName={'Click Attribution'}
          flex={1}
          getValue={(v = 0) => `${v} days`}
        />
        <DataGrid.Column
          field={COLUMN_DAYS_BETWEEN_REMARKETING}
          type={'text'}
          headerName={'Days Between Remarketing'}
          flex={1}
          getValue={(v = 0) => `${v} days`}
        />
        <DataGrid.Column
          field={COLUMN_TRACK_RE}
          type={'text'}
          headerName={'Track RE'}
          flex={1}
          renderCell={(p) => <TrackRe {...p} />}
        />

        <DataGrid.Column
          actionsColDef
          field={'actions'}
          type={'actions'}
          actionsComponent={ApplicationsActions}
          limit={1}
          onEdit={handleEdit}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default ApplicationsDataTable;
